/**
 * <cart-warning> — /cart page checkout enhancement.
 *
 * Rebuilt self-contained from live's component-cart-warning.js, which extended
 * ComponentCartBase — a class whose script only loads when
 * cart_type == "notification", so on live the define() threw and the /cart
 * checkout button was dead (O-10/O-11 root cause). No base class here, no
 * import graph, nothing shared to break.
 *
 * The checkout button is a NATIVE submit (name="checkout") — checkout works
 * with no JavaScript at all. This element only:
 *  - intercepts the click while the sustainability warning applies (a cart
 *    line whose product is tagged `sustainable_warning` carries
 *    _upsell == 'true') and shows live's warning modal instead;
 *  - dispatches the auxiliary `checkout_click` when checkout proceeds;
 *    Shopify Customer Events remains the sole owner of `begin_checkout`;
 *  - on "do not add": removes the flagged lines (rendered server-side into
 *    data-warning-lines) via ONE cart/change.js call per line, then submits.
 *    Live dispatched an event into a dead component and blind-submitted after
 *    a 1s timer; this waits for the removal round-trip instead. No retry.
 */
const CART_WARNING_CHECKOUT_CONTEXT_TIMEOUT_MS = 1500;

const waitForCartWarningTrackingContext = () => {
  const write = window.shekoTracking?.persistCartContext?.();
  if (!write || typeof write.then !== 'function') return Promise.resolve(false);
  return Promise.race([
    write,
    new Promise((resolve) => setTimeout(() => resolve(false), CART_WARNING_CHECKOUT_CONTEXT_TIMEOUT_MS)),
  ]);
};

if (!customElements.get('cart-warning')) {
  class CartWarning extends HTMLElement {
    connectedCallback() {
      const configScript = this.querySelector('[data-cart-warning-configuration]');
      try {
        this.configuration = JSON.parse(configScript.textContent);
      } catch {
        this.configuration = { upsell_warning_active: false };
      }
      this.submitButton = this.querySelector('[data-checkout]');
      this.proceedButton = this.querySelector('[data-warning-proceed]');
      this.modalContainer = this.querySelector('[data-warning-modal-container]');
      this.modal = this.querySelector('[data-warning-modal]');
      this.removeItems = this.querySelector('[data-remove-items]');

      this.submitButton?.addEventListener('click', this.handleSubmit.bind(this));
      this.proceedButton?.addEventListener('click', this.handleProceed.bind(this));
      this.removeItems?.addEventListener('click', this.handleRemoveItems.bind(this));
      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this.modal) this.hide();
      });
      document.addEventListener('cart-warning-enable', () => {
        this.configuration.upsell_warning_active = true;
      });
      document.addEventListener('cart-warning-disable', () => {
        this.configuration.upsell_warning_active = false;
      });
    }

    async handleSubmit(event) {
      if (this.configuration.upsell_warning_active) {
        event.preventDefault();
        this.show();
        return;
      }
      event.preventDefault();
      if (window.sessionStorage.getItem('countdown')) window.sessionStorage.removeItem('countdown');
      await this.proceedToCheckout(this.submitButton);
    }

    async handleProceed(event) {
      event.preventDefault();
      await this.proceedToCheckout(this.proceedButton);
    }

    async proceedToCheckout(submitter) {
      this.dispatchCheckoutClick();
      await waitForCartWarningTrackingContext();
      window.location.assign('/checkout');
    }

    dispatchCheckoutClick() {
      document.dispatchEvent(new CustomEvent('checkout_click', { bubbles: true }));
    }

    async handleRemoveItems() {
      const lines = (this.dataset.warningLines || '')
        .split(',')
        .map((line) => parseInt(line, 10))
        .filter((line) => !Number.isNaN(line))
        // Remove from the highest line number down so earlier indexes stay valid.
        .sort((a, b) => b - a);

      try {
        for (const line of lines) {
          await fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({ line, quantity: 0 }),
          });
        }
      } catch (error) {
        console.error('Error:', error);
      }
      this.hide();
      await this.proceedToCheckout(this.proceedButton);
    }

    show() {
      this.modalContainer.setAttribute('open', '');
      this.modalContainer.style.position = 'fixed';
    }

    hide() {
      this.modalContainer.removeAttribute('open');
      this.modalContainer.style.position = 'absolute';
    }
  }

  customElements.define('cart-warning', CartWarning);
}
